import React, { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n';
import { useUnsavedChanges } from '../shared/UnsavedChangesGuard';
import { DAILY_LOG, SUBQ_FLUIDS } from '../../lib/vocabulary';

interface NavItem {
  label: string;
  path: string;
  icon: React.ReactNode;
}

const mainNavItems: NavItem[] = [
  {
    get label() { return i18n.t('nav.items.dashboard'); }, path: '/dashboard',
    icon: <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.75"><rect x="3" y="3" width="7" height="7" rx="1" /><rect x="14" y="3" width="7" height="7" rx="1" /><rect x="3" y="14" width="7" height="7" rx="1" /><rect x="14" y="14" width="7" height="7" rx="1" /></svg>,
  },
  {
    get label() { return i18n.t('nav.items.labs'); }, path: '/lab-results',
    icon: <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.75"><path strokeLinecap="round" strokeLinejoin="round" d="M14.5 2v17.5c0 1.4-1.1 2.5-2.5 2.5h0c-1.4 0-2.5-1.1-2.5-2.5V2" /><path strokeLinecap="round" d="M8.5 2h7" /><path strokeLinecap="round" d="M14.5 16h-5" /></svg>,
  },
  {
    get label() { return DAILY_LOG.nav; }, path: '/daily-log',
    icon: <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.75"><rect width="8" height="4" x="8" y="2" rx="1" /><path strokeLinecap="round" d="M16 4h2a2 2 0 012 2v14a2 2 0 01-2 2H6a2 2 0 01-2-2V6a2 2 0 012-2h2" /><path strokeLinecap="round" d="M12 11h4M12 16h4M8 11h.01M8 16h.01" /></svg>,
  },
  {
    get label() { return i18n.t('nav.items.medications'); }, path: '/medications',
    icon: <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.75"><path strokeLinecap="round" strokeLinejoin="round" d="m10.5 20.5 10-10a4.95 4.95 0 1 0-7-7l-10 10a4.95 4.95 0 1 0 7 7Z" /><path strokeLinecap="round" d="m8.5 8.5 7 7" /></svg>,
  },
];

const moreNavItems: NavItem[] = [
  {
    get label() { return i18n.t('nav.items.guidance'); }, path: '/guidance',
    icon: <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.75"><path strokeLinecap="round" strokeLinejoin="round" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" /></svg>,
  },
  {
    get label() { return i18n.t('nav.items.howToGuides'); }, path: '/how-to',
    icon: <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.75"><circle cx="12" cy="12" r="9" /><circle cx="12" cy="12" r="3.5" /><path strokeLinecap="round" d="m5.6 5.6 3.9 3.9M18.4 5.6l-3.9 3.9M18.4 18.4l-3.9-3.9M5.6 18.4l3.9-3.9" /></svg>,
  },
  {
    get label() { return i18n.t('nav.items.vetVisits'); }, path: '/vet-visits',
    icon: <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.75"><rect x="3" y="4" width="18" height="18" rx="2" /><path strokeLinecap="round" d="M16 2v4M8 2v4M3 10h18" /><path strokeLinecap="round" d="M9 16l2 2 4-4" /></svg>,
  },
  {
    get label() { return i18n.t('nav.items.weight'); }, path: '/weight',
    icon: <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.75"><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" strokeLinecap="round" /></svg>,
  },
  {
    get label() { return SUBQ_FLUIDS.label; }, path: '/subq-fluids',
    icon: <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.75"><path strokeLinecap="round" strokeLinejoin="round" d="M12 2.5s6 6.5 6 10.5a6 6 0 01-12 0c0-4 6-10.5 6-10.5z" /></svg>,
  },
  {
    get label() { return i18n.t('nav.items.bloodGlucose'); }, path: '/glucose',
    icon: <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.75"><path strokeLinecap="round" strokeLinejoin="round" d="M3 15l4-5 4 3 4-7 6 9" /></svg>,
  },
  {
    get label() { return i18n.t('nav.items.breathingRate'); }, path: '/breathing-rate',
    icon: <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.75"><path strokeLinecap="round" strokeLinejoin="round" d="M2 12h4l2-6 3 12 2-6h2a3 3 0 100-6" /></svg>,
  },
  {
    get label() { return i18n.t('nav.items.qualityOfLife'); }, path: '/qol',
    icon: <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.75"><path strokeLinecap="round" strokeLinejoin="round" d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0016.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 002 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z" /></svg>,
  },
  {
    get label() { return i18n.t('nav.items.vetVisitPrep'); }, path: '/vet-prep',
    icon: <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.75"><path strokeLinecap="round" d="M4.8 2.3A.3.3 0 105 2H4a2 2 0 00-2 2v5a6 6 0 006 6v0a6 6 0 006-6V4a2 2 0 00-2-2h-1a.2.2 0 10.3.3" /><path strokeLinecap="round" d="M8 15v1a6 6 0 006 6v0a6 6 0 006-6v-4" /><circle cx="20" cy="10" r="2" /></svg>,
  },
  {
    get label() { return i18n.t('nav.items.exportData'); }, path: '/export',
    icon: <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.75"><path strokeLinecap="round" strokeLinejoin="round" d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3" /></svg>,
  },
  {
    get label() { return i18n.t('nav.items.catProfile'); }, path: '/cat-profile',
    icon: <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.75"><path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" /></svg>,
  },
  {
    get label() { return i18n.t('nav.items.carePasses'); }, path: '/care-passes',
    icon: <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.75"><path strokeLinecap="round" strokeLinejoin="round" d="M15.75 5.25a3 3 0 013 3m3 0a6 6 0 01-7.029 5.912c-.563-.097-1.159.026-1.563.43L10.5 17.25H8.25v2.25H6v2.25H2.25v-2.818c0-.597.237-1.17.659-1.591l6.499-6.499c.404-.404.527-1 .43-1.563A6 6 0 1121.75 8.25z" /></svg>,
  },
  {
    get label() { return i18n.t('nav.items.settings'); }, path: '/settings',
    icon: <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.75"><path strokeLinecap="round" strokeLinejoin="round" d="M12.22 2h-.44a2 2 0 00-2 2v.18a2 2 0 01-1 1.73l-.43.25a2 2 0 01-2 0l-.15-.08a2 2 0 00-2.73.73l-.22.38a2 2 0 00.73 2.73l.15.1a2 2 0 011 1.72v.51a2 2 0 01-1 1.74l-.15.09a2 2 0 00-.73 2.73l.22.38a2 2 0 002.73.73l.15-.08a2 2 0 012 0l.43.25a2 2 0 011 1.73V20a2 2 0 002 2h.44a2 2 0 002-2v-.18a2 2 0 011-1.73l.43-.25a2 2 0 012 0l.15.08a2 2 0 002.73-.73l.22-.39a2 2 0 00-.73-2.73l-.15-.08a2 2 0 01-1-1.74v-.5a2 2 0 011-1.74l.15-.09a2 2 0 00.73-2.73l-.22-.38a2 2 0 00-2.73-.73l-.15.08a2 2 0 01-2 0l-.43-.25a2 2 0 01-1-1.73V4a2 2 0 00-2-2z" /><circle cx="12" cy="12" r="3" /></svg>,
  },
  // Last in the sheet, mirroring the sidebar's own section for it.
  {
    get label() { return i18n.t('nav.items.caringForYou'); }, path: '/caring-for-you',
    icon: <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.75"><path strokeLinecap="round" strokeLinejoin="round" d="M4 9h12v5.5a4.5 4.5 0 01-4.5 4.5h-3A4.5 4.5 0 014 14.5V9z" /><path strokeLinecap="round" d="M16 10.5h1.5a2.5 2.5 0 010 5H16" /><path strokeLinecap="round" d="M7.5 3v2.5M12.5 3v2.5" /></svg>,
  },
];

export const BottomNav: React.FC = () => {
  const { t } = useTranslation();
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const { confirmDiscard } = useUnsavedChanges();
  const navigate = useNavigate();

  // The unsaved-changes prompt is the app's own dialog, so a guarded link always
  // cancels its own click and replays the navigation once the owner answers.
  const guardedNavigate = (e: React.MouseEvent, path: string) => {
    e.preventDefault();
    confirmDiscard().then((ok) => {
      if (!ok) return;
      setShowMoreMenu(false);
      navigate(path);
    });
  };

  return (
    <>
      {/* Bottom Tab Bar */}
      <nav className="fixed bottom-0 left-0 right-0 bg-[rgba(253,251,247,0.92)] backdrop-blur-xl border-t border-faint safe-area-inset-bottom z-50">
        <div className="flex justify-around items-stretch">
          {mainNavItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              className={({ isActive }) =>
                `flex flex-col items-center justify-center flex-1 min-w-0 px-0.5 min-h-14 gap-0.5 transition-all duration-200 ${
                  isActive ? 'text-sage-600' : 'text-soft hover:text-mid active:text-charcoal'
                }`
              }
              onClick={(e) => guardedNavigate(e, item.path)}
            >
              {item.icon}
              <span className="max-w-full text-2xs font-semibold leading-tight text-center">{item.label}</span>
            </NavLink>
          ))}

          {/* More Button */}
          <button
            onClick={() => setShowMoreMenu(!showMoreMenu)}
            aria-expanded={showMoreMenu}
            className={`flex flex-col items-center justify-center flex-1 min-w-0 px-0.5 min-h-14 gap-0.5 transition-all duration-200 ${
              showMoreMenu ? 'text-sage-600' : 'text-soft hover:text-mid'
            }`}
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="1" /><circle cx="19" cy="12" r="1" /><circle cx="5" cy="12" r="1" />
            </svg>
            <span className="max-w-full text-2xs font-semibold leading-tight text-center">{t('nav.bottomNav.more')}</span>
          </button>
        </div>
      </nav>

      {/* More Menu Sheet */}
      {showMoreMenu && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-charcoal/30 backdrop-blur-sm z-40 md:hidden animate-fade-in"
            onClick={() => setShowMoreMenu(false)}
          />

          {/* Sheet Content */}
          <div className="fixed bottom-14 left-0 right-0 bg-white rounded-t-3xl shadow-lifted z-50 max-h-[60vh] overflow-y-auto md:hidden animate-slide-up safe-area-inset-bottom">
            <div className="p-5">
              <div className="flex justify-center mb-4">
                <div className="w-10 h-1 bg-slate-200 rounded-full" />
              </div>

              <h2 className="text-sm font-semibold text-soft uppercase tracking-wider mb-3 px-1">{t('nav.bottomNav.moreOptions')}</h2>

              <div className="space-y-1">
                {moreNavItems.map((item) => (
                  <NavLink
                    key={item.path}
                    to={item.path}
                    className={({ isActive }) =>
                      `flex items-center gap-3.5 px-4 py-3 rounded-xl transition-all duration-200 font-medium text-sm ${
                        isActive
                          ? 'bg-sage-50 text-sage-700'
                          : 'text-charcoal hover:bg-sage-50'
                      }`
                    }
                    onClick={(e) => guardedNavigate(e, item.path)}
                  >
                    <span className="opacity-70">{item.icon}</span>
                    <span>{item.label}</span>
                  </NavLink>
                ))}
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};
